/**
 * Colonnes du drill-down sur un référentiel d'OCCURRENCES : lesquelles, et dans quel ordre.
 *
 * Pourquoi ce module existe : la modale n'affiche que les **10 premières** colonnes que l'ETL lui
 * renvoie (`DrillDownModal/index.tsx`), et l'ordre venait jusqu'ici de celui des champs du
 * formulaire UX — c'est-à-dire de rien de significatif. Sur un référentiel d'occurrences, généré par
 * la plateforme, ces dix places étaient donc occupées par des identifiants techniques
 * (`evaluationRunId`, `questionsSchemaId`, `responsesTableId`…) tandis que le plan de contrôle, le
 * statut et les dates tombaient hors de la fenêtre.
 *
 * ⚠️ Deux problèmes distincts, et le second n'est pas un problème d'ordre. Certaines colonnes utiles
 * ne sont **pas dans le formulaire** : « Jours de retard » est créée sur le référentiel sans y être
 * ajoutée, et le libellé du contrôle vit sur un référentiel **joint** (`loaded_*`), que le
 * drill-down d'un référentiel direct écarte par principe. Aucun reclassement ne peut faire
 * apparaître ce qui n'a jamais été demandé à l'ETL : les colonnes métier sont donc cherchées parmi
 * **toutes** les colonnes du cube, pas seulement parmi celles du formulaire.
 *
 * ⚠️ L'identité d'une colonne est son **rôle**, jamais son nom : celui-ci est renommable par le
 * client et diffère d'un tenant à l'autre. Deux signaux indépendants, tous deux produits côté back
 * (`occurrence-columns.constants.ts`, `control-columns.constants.ts`) : `extra.role`
 * (« occurrence.statusLabel », « control.codeEtLibelle ») et l'identifiant de colonne
 * (« statusLabel »). En accepter les deux évite de dépendre d'un seul chemin de sérialisation.
 */

/**
 * Types de widgets qui ouvrent un drill-down au clic.
 *
 * ⚠️ Partagée entre l'EXÉCUTION (`containers/Widget`, qui câble `onSegmentClick`) et l'ÉCRAN DE
 * CONFIGURATION (qui décide d'afficher ou non le réglage des colonnes). La liste vivait en dur dans
 * le JSX : deux copies auraient divergé en silence, offrant un réglage sans effet sur un widget qui
 * n'ouvre rien — ou l'inverse.
 */
export const TYPES_AVEC_DRILL_DOWN = [
  "pieChart",
  "semiPieChart",
  "doublePieChart",
  "doubleSemiPieChart",
  "histogram",
  "stackedBar",
  "pyramide",
  "counter",
  "heatMapRisk",
  "ganttChartV2",
  "lineChart",
]

/**
 * Ordre de lecture voulu, du plus parlant au plus accessoire.
 *
 * Les SEPT PREMIÈRES sont la liste nommée par le client (2026-09-07) : plan, contrôle, date de
 * début, date de fin, entité, responsable, assigné. Le reste suit dans un ordre de lecture — statut,
 * période de réalisation, retard, taux — et occupe les places qui restent dans la fenêtre visible.
 *
 * ⚠️ « Assigné » est la colonne `controleurLabel`, **nommée « Contrôleur » par défaut** mais
 * alimentée par les assignés — personnes et groupes (`assignesLabel`, back). Il n'existe pas d'autre
 * colonne d'assignation : chercher un « assigneeLabel » serait une impasse.
 *
 * ⚠️ Ce sont bien `startDate` / `endDate` qui sont demandées, PAS la paire de réalisation — décision
 * du 2026-09-07, qui INVERSE celle de la veille. Les deux paires étant égales partout sur le tenant
 * CRC, c'est la paire non demandée qui est reléguée, pour ne pas dépenser deux places visibles à
 * répéter la même information.
 */
const PRIORITE_METIER = [
  // --- les sept colonnes nommées par le client, dans son ordre ---
  "planLabel",
  "control.codeEtLibelle",
  "startDate",
  "endDate",
  "entity.libelle",
  "responsableLabel",
  "controleurLabel",
  // --- le reste, pour les places qui restent ---
  "statusLabel",
  "daysLate",
  "conformityValue",
  "resultBand",
  "isLate",
  "conformityLevel",
  "responseRate",
  "escalationRate",
  "missed",
  "frequencyLabel",
]

/**
 * Doublons redondants : la même information qu'une colonne déjà prioritaire, sous un autre nom.
 *
 * Ils passent DERRIÈRE les colonnes propres du client — celles-ci disent quelque chose que la
 * plateforme ignore — mais devant les identifiants techniques, car ils restent lisibles.
 */
const REDONDANTES = new Set(["periodStart", "periodEnd"])

/**
 * Colonnes métier qui ne sont pas forcément dans le formulaire UX, et qu'il faut donc aller chercher
 * parmi toutes les colonnes du cube pour qu'elles puissent s'afficher.
 *
 * Volontairement restreint : chaque entrée ajoute une colonne à la requête ETL, et une colonne
 * ajoutée à tort est du travail serveur pour rien.
 */
const A_GARANTIR = new Set([
  "planLabel",
  "control.codeEtLibelle",
  "entity.libelle",
  "startDate",
  "endDate",
  "responsableLabel",
  "controleurLabel",
  "statusLabel",
  "isLate",
  "daysLate",
])

/**
 * Colonnes brutes et leur jumelle lisible.
 *
 * ⚠️ La brute n'est reléguée QUE si sa jumelle est réellement présente. Un tenant qui n'a pas encore
 * les colonnes de lisibilité (elles datent du lot L2) n'a que `status` : la reléguer là-bas
 * remplacerait une valeur technique par AUCUNE valeur, ce qui est pire que le défaut qu'on corrige.
 */
const JUMELLE_LISIBLE = {
  status: "statusLabel",
  periodicity: "frequencyLabel",
}

/**
 * L'ENTITÉ n'a pas de rôle, contrairement au contrôle : on la reconnaît à son emplacement.
 *
 * ⚠️ C'est le point FRAGILE de ce module, et il faut le savoir avant d'y toucher. Les colonnes du
 * contrôle portent un rôle stable (`control.codeEtLibelle`) parce que le back les fabrique ; celles
 * de l'entité sont les colonnes du CLIENT sur son référentiel Entités, sans identité propre. On les
 * reconnaît donc par le nom de leur jointure et par leur libellé — deux choses renommables.
 *
 * ⚠️ Le namespace doit être comparé EXACTEMENT. Le template Contrôle Interne V2 en crée **trois**
 * qui se ressemblent — `Contrôle lié.Entités.Entités`, `…Entité parente.Entités`,
 * `…Organisme.Entités` — et une correspondance approximative sur « Entités » désignerait l'entité
 * parente ou l'organisme aussi souvent que la bonne.
 *
 * En cas de renommage côté client, la colonne cesse simplement d'apparaître : une absence, pas une
 * valeur fausse. Le correctif durable est de donner un rôle à ce libellé côté back, comme pour le
 * contrôle — voir `control-columns.constants.ts`.
 */
const NAMESPACE_ENTITE = "Contrôle lié.Entités.Entités"
const LIBELLES_ENTITE = ["libellé", "libelle", "label", "nom", "name"]
const IDENTITE_ENTITE = "entity.libelle"

/**
 * Identifiants qui ne disent rien à personne : clés étrangères et rouages internes. Ils restent
 * demandés à l'ETL quand le formulaire les porte — certains servent aux jointures — mais passent
 * derrière tout le reste.
 */
const TECHNIQUES = new Set([
  // ⚠️ DEUX clés pour UNE colonne, et ce n'est pas une redondance à nettoyer. C'est la seule
  // colonne d'occurrence dont le rôle et l'identifiant divergent : rôle `occurrence.id` (donc
  // suffixe « id ») mais identifiant `occurenceId`. Les 28 autres concordent. `identiteColonne`
  // préférant le rôle, n'inscrire que `occurenceId` laissait la colonne passer — c'est ce qui la
  // rendait encore visible après un premier correctif qui semblait pourtant complet.
  "id",
  "occurenceId",
  "evaluationRunId",
  "evaluationId",
  "questionsSchemaId",
  "responsesTableId",
  "assigneesIds",
  "responsableId",
  "filtersDossiers",
  "kpiResult",
  "planId",
])

/**
 * L'identité métier d'un champ, ou `null`.
 *
 * Les rôles `control.*` sont rendus **entiers** : ils vivent sur un autre référentiel, et rabattre
 * les deux espaces de noms sur un seul risquerait de confondre deux colonnes homonymes.
 */
export function identiteColonne(champ) {
  const role = champ?.infoField?.extra?.role
  if (typeof role === "string" && role) {
    if (role.startsWith("occurrence.")) return role.slice("occurrence.".length)
    return role
  }
  // L'entité, faute de rôle, se reconnaît à sa jointure ET à son libellé (cf. le bloc ci-dessus).
  const namespace = champ?.dataSource?.name ?? champ?.dataSourceUUID
  if (namespace === NAMESPACE_ENTITE) {
    const libelle = String(champ?.name ?? "").trim().toLowerCase()
    if (LIBELLES_ENTITE.includes(libelle)) return IDENTITE_ENTITE
  }

  // Repli sur l'identifiant de colonne, qui porte la même valeur sans le préfixe. Utile si `extra`
  // ne traverse pas la sérialisation : mieux vaut deux chemins qu'un point de rupture unique.
  const id = champ?.infoField?.id
  return typeof id === "string" && id ? id : null
}

/**
 * Cette colonne est-elle un identifiant technique, à ne JAMAIS afficher ?
 *
 * Sert à l'écarter de la sélection, et pas seulement à la reléguer : `occurenceId` remontait en tête
 * de la fenêtre visible parce qu'un compteur le déclare dans ses données — et la bande « technique »
 * ne protège que ce qui vient du formulaire.
 *
 * ⚠️ Ne couvre pas `idSample` ni les `loaded_*.idSample` : ceux-là servent aux jointures et à la
 * navigation, et la modale les masque déjà d'elle-même.
 */
export function estColonneTechnique(champ) {
  const identite = identiteColonne(champ)
  return Boolean(identite) && TECHNIQUES.has(identite)
}

/**
 * Libellés imposés dans le drill-down, par identité de colonne.
 *
 * Les noms d'origine décrivent la colonne dans son référentiel — « Code — Libellé » est juste dans
 * une liste déroulante de filtre — mais pas ce qu'elle représente dans une liste d'évaluations, où
 * le lecteur attend « Contrôle ». On surcharge donc l'affichage **ici seulement** : renommer les
 * colonnes elles-mêmes toucherait les filtres, les widgets et le référentiel du client.
 */
const LIBELLES_DRILLDOWN = {
  "control.codeEtLibelle": { cle: "DRILLDOWN_COL_CONTROL", defaut: "Control" },
  "entity.libelle": { cle: "DRILLDOWN_COL_ENTITY", defaut: "Entity" },
}

/**
 * Le libellé à afficher pour cette colonne dans le drill-down, ou `null` pour garder le sien.
 *
 * Trois sources, dans cet ordre :
 *
 *  1. le **renommage du widget**, s'il en porte un pour cette colonne ;
 *  2. le libellé imposé par la plateforme (table ci-dessus) ;
 *  3. rien — l'appelant garde le nom du référentiel.
 *
 * ⚠️ **Le renommage est un libellé d'AFFICHAGE, et rien d'autre.** Il ne touche ni la colonne du
 * référentiel, ni les filtres, ni les autres widgets : c'est ce qui le distingue d'un renommage de
 * colonne, lequel se propage partout et peut créer des homonymes. La recherche de la modale, elle,
 * porte sur `uuid` et jamais sur le nom affiché — renommer un en-tête ne peut donc pas la casser.
 *
 * ⚠️ Les clés du renommage sont celles de `cleColonneDrillDown`, comme `drillDownColumns` : les
 * deux réglages désignent les mêmes colonnes et doivent le faire de la même façon. L'identité nue
 * est acceptée en repli, pour un réglage posé avant la qualification.
 *
 * @param champ       le champ, tel qu'il vient de `fieldsData`
 * @param trad        le dictionnaire de traduction courant
 * @param renommages  `{ clé de colonne: libellé }`, le réglage du widget
 */
export function libelleDrillDown(champ, trad, renommages) {
  if (renommages && typeof renommages === "object") {
    for (const cle of [cleColonneDrillDown(champ), identiteColonne(champ)]) {
      const voulu = cle ? renommages[cle] : undefined
      // Une chaîne vide vaut « pas de renommage » : c'est ce que laisse un champ de saisie qu'on
      // vide, et le lecteur attend alors de retrouver le nom d'origine, pas un en-tête sans texte.
      if (typeof voulu === "string" && voulu.trim() !== "") return voulu.trim()
    }
  }

  const impose = LIBELLES_DRILLDOWN[identiteColonne(champ)]
  if (!impose) return null
  return trad?.[impose.cle] || impose.defaut
}

/** Espace de nom BRUT d'une colonne, tel que le cube le porte. */
function espaceBrut(champ) {
  return String(champ?.dataSource?.name ?? champ?.dataSourceUUID ?? "").trim()
}

/**
 * Espace de nom sous une forme lisible dans une liste déroulante.
 *
 * Le format du cube est `<chemin de relations>.<nom du référentiel joint>`. C'est le CHEMIN qui
 * distingue des colonnes homonymes ; le dernier segment ne fait que nommer le référentiel au bout,
 * et sur ce tenant il vaut « Entités » pour les trois jointures à distinguer — donc il n'apprend
 * rien et allonge chaque entrée. On le retire :
 *
 *     Contrôle lié.Entités.Entités                  →  Contrôle lié.Entités
 *     Contrôle lié.Entités.Organisme.Entités         →  Contrôle lié.Entités.Organisme
 *     Contrôle lié.Entités.Entité parente.Entités    →  Contrôle lié.Entités.Entité parente
 *     Occurrences                                    →  Occurrences   (racine, un seul segment)
 *
 * ⚠️ Ce raccourci sert au LIBELLÉ seulement, jamais à la clé : deux chemins distincts pourraient en
 * théorie se raccourcir pareil, et une clé ambiguë est exactement le défaut qu'on répare ici.
 */
function espaceLisible(champ) {
  const brut = espaceBrut(champ)
  if (brut === "") return ""
  const segments = brut.split(".")
  if (segments.length >= 2) segments.pop()
  return segments.join(".")
}

/**
 * La clé sous laquelle une colonne est DÉSIGNÉE dans un réglage, ou `null`.
 *
 * ⚠️ Ce n'est pas `identiteColonne`, et la différence est un défaut mesuré, pas une précaution :
 * pour une colonne SANS rôle, `infoField.id` est un **GUID de colonne**, et le même GUID revient sur
 * chaque jointure d'un référentiel monté plusieurs fois. Sur le tenant CRC, les « Libellé » de
 * « Entités », « Entité parente » et « Organisme » partagent une seule identité : choisir l'un
 * rendait l'autre, en silence. La clé porte donc l'espace de nom.
 *
 * ⚠️ Une colonne PORTÉE PAR LA PLATEFORME (qui a un `role`) garde sa clé nue : le rôle est unique
 * par construction et surtout **portable** d'un tenant à l'autre, ce qu'un GUID n'est pas. C'est ce
 * qui permet à un modèle de dashboard de déclarer un réglage qui survive à l'installation.
 */
export function cleColonneDrillDown(champ) {
  const identite = identiteColonne(champ)
  if (!identite) return null

  const role = champ?.infoField?.extra?.role
  if (typeof role === "string" && role) return identite
  if (identite === IDENTITE_ENTITE) return identite

  // ⚠️ L'espace BRUT, pas le raccourci d'affichage : la clé doit rester non ambiguë.
  const espace = espaceBrut(champ)
  return espace ? `${espace}::${identite}` : identite
}

/**
 * Les colonnes proposables au réglage, dédupliquées par CLÉ et triées par libellé.
 *
 * ⚠️ Les identifiants techniques en sont écartés : les offrir au réglage reviendrait à proposer de
 * reproduire le défaut que ce module corrige. `idSample` et les colonnes système le sont aussi —
 * ils servent aux jointures et à la navigation, et la modale les masque déjà.
 *
 * @param tousLesChamps `fieldsData`, la liste complète des colonnes du cube
 * @param trad          le dictionnaire courant, pour les libellés imposés du drill-down
 * @returns `[{ value: identité, label }]`, prêt pour une liste déroulante
 */
export function optionsColonnesDrillDown(tousLesChamps, trad) {
  const vues = new Set()
  const candidats = []
  for (const champ of Array.isArray(tousLesChamps) ? tousLesChamps : []) {
    const uuid = String(champ?.uuid ?? "")
    if (uuid === "" || uuid.startsWith("_")) continue
    if (uuid === "idSample" || uuid.endsWith(".idSample")) continue

    const identite = identiteColonne(champ)
    if (!identite || TECHNIQUES.has(identite)) continue

    const cle = cleColonneDrillDown(champ)
    if (!cle || vues.has(cle)) continue

    vues.add(cle)
    candidats.push({
      cle,
      nom: String(libelleDrillDown(champ, trad) ?? champ?.name ?? identite),
      espace: espaceLisible(champ),
    })
  }

  /**
   * Un libellé qui revient plusieurs fois est QUALIFIÉ par son espace de nom.
   *
   * Sans cela la liste offrait cinq « Libellé », trois « Statut » et trois « Code » indiscernables
   * — le lecteur ne pouvait pas savoir lequel désignait quoi. La qualification n'est posée que sur
   * les libellés ambigus : l'ajouter partout allongerait toutes les entrées pour rien.
   */
  const occurrences = new Map()
  for (const c of candidats) occurrences.set(c.nom, (occurrences.get(c.nom) ?? 0) + 1)

  return candidats
    .map(c => ({
      value: c.cle,
      label: occurrences.get(c.nom) > 1 && c.espace ? `${c.nom} — ${c.espace}` : c.nom,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Résout une liste d'identités choisies en colonnes réelles, dans l'ordre demandé.
 *
 * ⚠️ Une identité qui ne correspond à aucune colonne est IGNORÉE, jamais une erreur : un référentiel
 * dont on retire une colonne ne doit pas casser la modale de tous les widgets qui la citaient.
 *
 * ⚠️ À identité égale, la FICHE l'emporte sur le reste du cube — c'est la colonne que le référentiel
 * du dashboard expose vraiment, et non son homonyme sur un référentiel joint.
 *
 * ⚠️ La sortie suit l'ORDRE DE LA LISTE REÇUE : c'est l'ordre dans lequel le client a désigné ses
 * colonnes, et c'est celui qu'il attend. L'écran de configuration le préserve en présentant à la
 * liste déroulante ses options « choisies d'abord » — voir `ConfigDrillDownColumns`.
 *
 * ⚠️ Deux formes de clé sont acceptées : la clé qualifiée de `cleColonneDrillDown` et l'identité
 * nue. Un réglage posé avant la qualification reste donc lisible, au lieu de se vider sans un mot.
 */
function resoudreIdentites(identites, champsFiche, tousLesChamps) {
  const parCle = new Map()
  const parIdentite = new Map()
  const indexer = champs => {
    for (const champ of Array.isArray(champs) ? champs : []) {
      const cle = cleColonneDrillDown(champ)
      if (cle && !parCle.has(cle)) parCle.set(cle, champ)
      const identite = identiteColonne(champ)
      if (identite && !parIdentite.has(identite)) parIdentite.set(identite, champ)
    }
  }
  indexer(champsFiche)
  indexer(tousLesChamps)

  const vues = new Set()
  const sortie = []
  for (const demandee of identites) {
    const champ = parCle.get(demandee) ?? parIdentite.get(demandee)
    if (!champ || vues.has(champ)) continue
    vues.add(champ)
    sortie.push(champ)
  }
  return sortie
}

/** Rôle de la colonne qui identifie le contrôle évalué par une occurrence. */
const ROLE_CONTROLE = "control.codeEtLibelle"

/**
 * Où mène le clic sur une ligne du drill-down, quand la liste porte des occurrences.
 *
 * Une occurrence est un **passage d'évaluation** : sa fiche n'apprend rien à qui vient de cliquer
 * sur « 12 évaluations non conformes ». Ce qu'on veut ouvrir, c'est le **contrôle** évalué. La
 * modale ne se sert de `referentialId` et `idSampleKey` que pour cette navigation — la liste, elle,
 * reste pilotée par le corps ETL — donc rediriger ne change rien à ce qui s'affiche.
 *
 * ⚠️ Rend `null` dès que le moindre élément manque plutôt qu'une cible partielle : un
 * `referentialId` de contrôle associé à l'identifiant d'une occurrence ouvrirait une **fiche
 * fausse**, ou vide, sans qu'aucune erreur ne le signale.
 *
 * ⚠️ **La colonne de rôle n'a PAS à être affichée.** Elle sert à identifier la jointure des
 * contrôles, pas à porter l'identifiant : celui-ci est l'`idSample` de la jointure, que le
 * drill-down sélectionne dès qu'UNE colonne de cette jointure est affichée. Exiger la colonne de
 * rôle elle-même parmi les colonnes affichées a cassé la navigation le 24/09/2026 : le client a
 * remplacé `Code — Libellé` par « Code » + « Libellé » (colonnes sans rôle, même jointure), et
 * chaque clic ouvrait la fiche brute de l'occurrence — une page d'erreur (recette du 13/10/2026).
 *
 * @param champs       les colonnes réellement sélectionnées pour le drill-down
 * @param tousLesChamps toutes les colonnes du cube (`fieldsData`), où chercher la colonne de rôle
 *   quand elle n'est pas affichée. Omis : seule la liste affichée est examinée.
 * @returns `{ referentialId, idSampleKey }`, ou `null` si aucune colonne affichée ne vient de la
 *   jointure des contrôles
 */
export function cibleNavigationControle(champs, tousLesChamps) {
  const affiches = Array.isArray(champs) ? champs : []
  const estRole = c => identiteColonne(c) === ROLE_CONTROLE
  const champ =
    affiches.find(estRole) ?? (Array.isArray(tousLesChamps) ? tousLesChamps : []).find(estRole)
  if (!champ) return null

  // La colonne doit venir d'un référentiel JOINT : c'est son préfixe qui donne l'`idSample` du
  // contrôle. Une colonne de contrôle non jointe n'aurait pas d'identifiant propre dans la ligne.
  const uuid = String(champ.uuid ?? "")
  if (!uuid.startsWith("loaded_") || !uuid.includes(".")) return null

  const referentialId = champ?.dataSource?.referentialId
  if (!referentialId) return null

  const prefixe = uuid.split(".").slice(0, -1).join(".")

  // ⚠️ L'`idSample` de la jointure n'est présent dans la ligne que si une colonne AFFICHÉE en vient
  // (`containers/Widget` le sélectionne à partir des colonnes affichées). Sans elle, la cible
  // désignerait une clé absente : la modale ne naviguerait pas, et la cible d'origine — la fiche
  // de l'occurrence — est alors la seule à conserver.
  // ⚠️ Préfixe ÉGAL, pas « commence par » : les colonnes d'entité sont jointes SOUS le contrôle
  // (`<préfixe>.loaded_left_….Libellé`), et leur `idSample` sélectionné est celui de l'entité.
  const prefixeDe = c => String(c?.uuid ?? "").split(".").slice(0, -1).join(".")
  const jointureAffichee = affiches.some(c => prefixeDe(c) === prefixe)
  if (!jointureAffichee) return null

  return { referentialId, idSampleKey: `${prefixe}.idSample` }
}

/** Le champ porte-t-il une identité que ce module sait classer ? */
const estConnue = identite =>
  Boolean(identite) &&
  (PRIORITE_METIER.includes(identite) ||
    TECHNIQUES.has(identite) ||
    REDONDANTES.has(identite) ||
    identite in JUMELLE_LISIBLE)

/**
 * Construit la liste ordonnée des colonnes du drill-down.
 *
 * Ne touche à rien si aucun champ ne porte d'identité connue : sur un référentiel métier du client,
 * l'ordre du formulaire est le sien et n'a pas à être réarrangé.
 *
 * Tri **stable** : deux champs de même rang gardent leur ordre d'entrée, donc les colonnes
 * personnalisées du client restent entre elles dans l'ordre de son formulaire.
 *
 * @param champsFiche  les champs du formulaire, dans leur ordre actuel (base de la liste)
 * @param tousLesChamps toutes les colonnes du cube, où puiser les colonnes métier absentes du
 *   formulaire. Omettre pour se limiter à un reclassement — c'est ce qu'il faut faire quand le
 *   drill-down cible un référentiel JOINT, dont les colonnes d'occurrence ne parlent pas.
 * @returns un nouveau tableau (les entrées ne sont pas modifiées)
 */
export function construireColonnesDrillDown(champsFiche, tousLesChamps, colonnesChoisies) {
  const base = Array.isArray(champsFiche) ? champsFiche : []

  /**
   * Un choix explicite REMPLACE la SÉLECTION automatique — il ne la complète pas.
   *
   * ⚠️ Compléter un choix par les colonnes « garanties » redonnerait à l'outil le dernier mot sur ce
   * que le client vient de désigner. Une liste vide vaut « pas de choix » et rend le comportement
   * automatique, ce qui laisse tous les dashboards existants inchangés.
   *
   * ⚠️ L'ORDRE est celui dans lequel les colonnes ont été désignées (voir `resoudreIdentites`).
   *
   * ⚠️ La recherche porte sur la fiche ET sur le cube, ce qui rend enfin désignable une colonne
   * qu'aucun reclassement ne pouvait faire apparaître — l'entité du contrôle lié, notamment, qui
   * devait jusqu'ici être déclarée dans les données du widget.
   */
  const choisies = (Array.isArray(colonnesChoisies) ? colonnesChoisies : [])
    .map(v => (typeof v === "string" ? v.trim() : ""))
    .filter(v => v !== "")
  if (choisies.length > 0) return resoudreIdentites(choisies, base, tousLesChamps)

  if (base.length === 0) return []

  const identites = new Map()
  let auMoinsUneConnue = false
  for (const champ of base) {
    const identite = identiteColonne(champ)
    identites.set(champ, identite)
    if (estConnue(identite)) auMoinsUneConnue = true
  }
  if (!auMoinsUneConnue) return [...base]

  // Colonnes métier manquantes, puisées dans le cube. Cherchées par identité, donc insensibles au
  // renommage — et ajoutées seulement si le cube les porte vraiment.
  const dejaPresentes = new Set([...identites.values()].filter(Boolean))
  const parUuid = new Set(base.map(c => c?.uuid).filter(Boolean))
  const ajouts = []
  for (const champ of Array.isArray(tousLesChamps) ? tousLesChamps : []) {
    const identite = identiteColonne(champ)
    if (!identite || !A_GARANTIR.has(identite)) continue
    if (dejaPresentes.has(identite) || parUuid.has(champ?.uuid)) continue
    identites.set(champ, identite)
    dejaPresentes.add(identite)
    parUuid.add(champ?.uuid)
    ajouts.push(champ)
  }

  const liste = [...base, ...ajouts]

  // Quatre bandes : métier connu (dans l'ordre voulu), colonnes du client, doublons redondants,
  // identifiants techniques.
  const RANG_RESTE = PRIORITE_METIER.length
  const RANG_REDONDANT = RANG_RESTE + 1
  const RANG_TECHNIQUE = RANG_RESTE + 2

  const rang = champ => {
    const identite = identites.get(champ)
    if (!identite) return RANG_RESTE

    const position = PRIORITE_METIER.indexOf(identite)
    if (position !== -1) return position

    if (TECHNIQUES.has(identite)) return RANG_TECHNIQUE
    if (REDONDANTES.has(identite)) return RANG_REDONDANT

    // Colonne brute : reléguée seulement si sa jumelle lisible est là pour prendre sa place.
    const jumelle = JUMELLE_LISIBLE[identite]
    if (jumelle) return dejaPresentes.has(jumelle) ? RANG_REDONDANT : RANG_RESTE

    return RANG_RESTE
  }

  return liste
    .map((champ, index) => ({ champ, index, rang: rang(champ) }))
    .sort((a, b) => a.rang - b.rang || a.index - b.index)
    .map(entree => entree.champ)
}

/**
 * Le référentiel que liste le drill-down, et s'il est JOINT au dashboard.
 *
 * Par défaut, la cible suit la donnée AFFICHÉE (`data[0]`) : un widget qui compte des plans
 * d'action sur un dashboard de recommandations liste des plans — le référentiel joint —, et seules
 * les colonnes de ce référentiel s'y résolvent.
 *
 * `widgetProps.drillDownOnRoot` (opt-in) force la cible sur le référentiel du DASHBOARD : la barre
 * « Reco X — 5 plans » liste alors la recommandation, avec les colonnes de la reco. Demandé par le
 * client CRC le 06/10/2026 pour « Top 10 des recommandations ayant le plus de plans d'actions », qui
 * devait offrir les mêmes colonnes que les autres widgets de la page.
 *
 * @param displayField          le champ de `data[0]`, tel que le cube le décrit (`fieldsData`)
 * @param dashboardReferentialId le référentiel racine du dashboard
 * @param surRacine             `widgetProps.drillDownOnRoot === true`
 * @returns `{ referentialId, isLinkedField }`
 */
export function cibleDrillDown(displayField, dashboardReferentialId, surRacine) {
  if (surRacine) return { referentialId: dashboardReferentialId, isLinkedField: false }
  const referentialId = displayField?.dataSource?.referentialId || dashboardReferentialId
  const isLinkedField =
    Boolean(referentialId) &&
    referentialId !== dashboardReferentialId &&
    String(displayField?.uuid ?? "").startsWith("loaded_")
  return { referentialId, isLinkedField }
}
